import { buildSourceMediaNode, type SourceMediaMountData } from '../source-media/core';
import { renderAdventureCardElement, type SharedAdventureCard } from '../shared/adventure-card';

type Nullable<T> = T | null | undefined;

interface TocEntry { id: string; text: string; level: number; }
interface LinkRow { id: string; label: string; href: string; sideInfo?: boolean; }
interface CreditBlock {
	kind: 'links' | 'text';
	singular?: string;
	plural?: string;
	title?: string;
	icon: string;
	tone: string;
	rows?: LinkRow[];
	label?: string;
	itemId?: string | null;
}
interface AssetRow { item_type: string; cnt: number; label: string; href?: string | null; }
interface AdventureContentRow { key: string; icon: string; label: string; cnt: number; }
interface SourceAdventure extends SharedAdventureCard {
	id: string;
	description: string;
	levelBegin: string | number;
	levelEnd: string | number;
	ageRange: string;
	href: string;
}
interface SourceDetailPayload {
	rootUrl: string;
	source: {
		id: string;
		name: string;
		abbreviation: string;
		description: string;
		media: SourceMediaMountData;
	};
	publisherLine: CreditBlock[];
	publication: {
		html: string;
		toc: TocEntry[];
	};
	rail: {
		price: { isFree: boolean; display: string };
		entitlement: {
			isNa: boolean;
			isSubscriptionLibrary: boolean;
			isCoreBookSubscription: boolean;
			viewerHasPlusCoreAccess: boolean;
			subscriptionUrl: string;
		};
		commerce: {
			loggedIn: boolean;
			eligible: boolean;
			mayBuy: boolean;
			inCart: boolean;
			inWishlist: boolean;
			loginUrl: string;
			cartUrl: string;
			wishlistUrl: string;
			libraryUrl?: string;
			inLibrary?: boolean;
		};
		assets: { total: number; rows: AssetRow[] };
		adventureContents: { total: number; rows: AdventureContentRow[] };
		credits: CreditBlock[];
	};
	adventures: SourceAdventure[];
}

declare global {
	interface Window {
		bootstrap?: {
			Offcanvas?: { getOrCreateInstance(el: Element): { hide(): void } };
			Collapse?: { getOrCreateInstance(el: Element): { show(): void } };
		};
		ftMountSourceDetailPage?: () => void;
		ftMountIcons?: (root?: Element | Document) => void;
		openItemInfo?: (itemId: string) => void;
		openSideInfoPanel?: () => void;
		forceOpenSideInfoPanel?: () => void;
		queryData?: (url: string, target: string, payload: Record<string, unknown>) => void;
		getSidelogContext?: () => Record<string, unknown> | null;
		addToSearchHistory?: (itemId: string) => void;
		initializeVideoCards?: () => void;
		BASE_URL?: string;
	}
}

function readPayload(): SourceDetailPayload | null {
	const el = document.getElementById('ft-source-detail-data');
	if (!el || !el.textContent) return null;
	try {
		const parsed = JSON.parse(el.textContent);
		return parsed && typeof parsed === 'object' ? parsed as SourceDetailPayload : null;
	} catch (e) {
		console.error('[source-detail] could not parse payload', e);
		return null;
	}
}

function esc(value: Nullable<unknown>): string {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');
}

function icon(name: Nullable<string>, width = 22, height = 22): string {
	const key = String(name || '').trim();
	if (!key) return '';
	return `<i class="ft-icon" data-icon="${esc(key)}" data-w="${width}" data-h="${height}" style="display:inline-block;width:${width}px;height:${height}px" aria-hidden="true"></i>`;
}

function renderMedia(data: SourceDetailPayload): string {
	const hasVideo = (data.source.media?.videos || []).some((v) => !!v?.url);
	const wrap = document.createElement('div');
	wrap.className = `source-rail-header-media w-100 rounded overflow-hidden border border-secondary border-opacity-50 mb-3${hasVideo ? ' video-card-hover' : ''}`;
	wrap.appendChild(buildSourceMediaNode(data.source.media || {}));
	return wrap.outerHTML;
}

function renderPriceBlock(data: SourceDetailPayload): string {
	const e = data.rail.entitlement;
	const c = data.rail.commerce;
	const price = data.rail.price;
	if (e.isNa) {
		return railBlock('Included', 'book', 'success', '<p class="mb-0 small text-white">Included in all accounts (no purchase).</p>');
	}
	if (e.isSubscriptionLibrary) {
		const body = e.isCoreBookSubscription
			? (e.viewerHasPlusCoreAccess
				? `<p class="mb-0 small text-white">Your account has access to all assets in this <strong>FateTide Core Book</strong> because of your <a class="link-light text-decoration-underline" href="${esc(e.subscriptionUrl)}">subscription</a>.</p>`
				: '<p class="mb-0 small text-white">This <strong>core book</strong> is part of the <strong>FateTide Plus</strong> library.</p>')
			: '<p class="mb-0 small text-dark">Included in <strong>FateTide Plus</strong></p>';
		return railBlock('Subscription', 'book', 'info', body);
	}
	let body = `<div class="text-center"><span class="fs-5 fw-semibold text-dark">${esc(price.display)}</span></div>`;
	if (price.isFree) {
		if (!c.loggedIn) {
			body += `<p class="mb-2 small text-white-50 mt-2">Log in to add this title to your library.</p><div class="d-flex flex-column gap-2"><a class="btn btn-outline-light w-100" href="${esc(c.loginUrl)}">Log in or sign up</a></div>`;
		} else {
			body += renderFreeLibraryActions(data);
		}
	} else if (c.eligible && !c.loggedIn) {
		body += `<p class="mb-2 small text-white-50 mt-2">Log in to add this title to your cart or wishlist.</p><div class="d-flex flex-column gap-2"><a class="btn btn-outline-light w-100" href="${esc(c.loginUrl)}">Log in or sign up</a></div>`;
	} else if (c.eligible && !c.mayBuy) {
		body += '<p class="mb-0 small text-white mt-2">You already have access to this title.</p>';
	} else if (c.eligible) {
		body += renderCommerceActions(data);
	}
	return railBlock('Price', price.isFree ? 'book' : 'add_to_cart', price.isFree ? 'success' : 'warning', body);
}

function renderFreeLibraryActions(data: SourceDetailPayload): string {
	const c = data.rail.commerce;
	const libraryUrl = c.libraryUrl || `${data.rootUrl}my_library.php`;
	return `<div class="d-flex flex-column gap-2 mt-2" id="ftSourceFreeLibraryActions" data-ft-source-id="${esc(data.source.id)}">
		<button type="button" class="btn btn-primary text-dark w-100 ft-src-add-free-library${c.inLibrary ? ' d-none' : ''}">${icon('book')} Add to My Library</button>
		<a class="btn btn-outline-primary w-100 ft-src-view-library${c.inLibrary ? '' : ' d-none'}" href="${esc(libraryUrl)}">${icon('book')} In library - view</a>
	</div>`;
}

function railBlock(title: string, iconKey: string, tone: string, body: string): string {
	return `<div class="list-group list-group-flush small mb-2 source-rail-credit-block rounded overflow-hidden border border-secondary border-opacity-50">
		<div class="list-group-item list-group-item-${esc(tone)} py-2 px-3 fw-semibold text-uppercase small d-flex align-items-center gap-2 border-bottom border-black border-opacity-10 source-rail-credit-block-header">
			<span class="flex-shrink-0" aria-hidden="true">${icon(iconKey, 18, 18)}</span><span>${esc(title)}</span>
		</div>
		<div class="list-group-item list-group-item-dark py-3 px-3 border-secondary border-opacity-25">${body}</div>
	</div>`;
}

function renderCommerceActions(data: SourceDetailPayload): string {
	const c = data.rail.commerce;
	return `<div class="d-flex flex-column gap-2 mt-2" id="ftSourceCommerceActions" data-ft-source-id="${esc(data.source.id)}">
		<button type="button" class="btn btn-primary text-dark w-100 ft-src-add-cart${c.inCart ? ' d-none' : ''}" data-ft-commerce-state="cart-out">${icon('add_to_cart')} Add to Cart</button>
		<a class="btn btn-outline-primary w-100 ft-src-view-cart${c.inCart ? '' : ' d-none'}" href="${esc(c.cartUrl)}" data-ft-commerce-state="cart-in">${icon('add_to_cart')} In cart - view</a>
		<button type="button" class="btn btn-dark btn-sm w-100 ft-src-remove-cart${c.inCart ? '' : ' d-none'}" data-ft-commerce-state="cart-in">Remove from cart</button>
		<button type="button" class="btn btn-warning text-dark w-100 ft-src-add-wishlist${c.inWishlist ? ' d-none' : ''}" data-ft-commerce-state="wish-out">${icon('add_to_wishlist')} Add to Wishlist</button>
		<a class="btn btn-outline-warning w-100 ft-src-view-wishlist${c.inWishlist ? '' : ' d-none'}" href="${esc(c.wishlistUrl)}" data-ft-commerce-state="wish-in">${icon('add_to_wishlist')} On wishlist - view</a>
		<button type="button" class="btn btn-dark btn-sm w-100 ft-src-remove-wishlist${c.inWishlist ? '' : ' d-none'}" data-ft-commerce-state="wish-in">Remove from wishlist</button>
	</div>`;
}

function renderCollapseList(id: string, title: string, total: number, rowsHtml: string, shown = false): string {
	if (!rowsHtml) return '';
	return `<div class="source-rail-assets-section mb-3 rounded overflow-hidden border border-secondary border-opacity-50">
		<button type="button" class="source-rail-assets-toggle btn btn-secondary w-100 rounded-0 border-0 d-flex align-items-center gap-2 py-2 px-3 text-start text-dark shadow-none" data-bs-toggle="collapse" data-bs-target="#${esc(id)}" aria-expanded="${shown ? 'true' : 'false'}" aria-controls="${esc(id)}">
			<span class="fw-bold text-uppercase small">${title}</span>
			<span class="badge bg-dark text-light">${total}</span>
			<span class="source-rail-assets-toggle-icon ms-auto user-select-none" aria-hidden="true">&#9660;</span>
		</button>
		<div id="${esc(id)}" class="collapse${shown ? ' show' : ''}">${rowsHtml}</div>
	</div>`;
}

function renderAssets(data: SourceDetailPayload): string {
	const rows = data.rail.assets.rows.map((r) => `<li class="list-group-item list-group-item-dark d-flex align-items-center justify-content-between gap-2 py-2 px-3">
		<span class="d-flex align-items-center gap-2 min-w-0"><span class="flex-shrink-0">${icon(r.item_type)}</span>${r.href ? `<a class="link-dark text-truncate text-decoration-none" href="${esc(r.href)}">${esc(r.label)}</a>` : `<span class="text-truncate">${esc(r.label)}</span>`}</span>
		<span class="badge bg-dark border border-secondary flex-shrink-0">${Number(r.cnt) || 0}</span>
	</li>`).join('');
	return renderCollapseList('sourceRailAssets', 'Assets', data.rail.assets.total, rows ? `<ul class="list-group list-group-flush list-group-item-dark small mb-0">${rows}</ul>` : '');
}

function renderAdventureContents(data: SourceDetailPayload): string {
	const rows = data.rail.adventureContents.rows.map((r) => `<li class="list-group-item list-group-item-dark d-flex align-items-center justify-content-between gap-2 py-2 px-3">
		<span class="d-flex align-items-center gap-2 min-w-0"><span class="flex-shrink-0">${icon(r.icon)}</span>${r.key === 'adventures' ? `<a class="link-dark text-truncate text-decoration-none" href="#sourceAdventures">${esc(r.label)}</a>` : `<span class="text-truncate">${esc(r.label)}</span>`}</span>
		<span class="badge bg-dark border border-secondary flex-shrink-0">${Number(r.cnt) || 0}</span>
	</li>`).join('');
	return renderCollapseList('sourceRailAdvContents', 'Adventure Contents', data.rail.adventureContents.total, rows ? `<ul class="list-group list-group-flush list-group-item-dark small mb-0">${rows}</ul>` : '');
}

function renderCreditBlock(block: CreditBlock): string {
	if (block.kind === 'links') {
		const rows = (block.rows || []).map((r) => `<a class="list-group-item py-2 px-3 border-secondary border-opacity-25 source-rail-credit-block__row${r.sideInfo ? ' ft-source-rail-sideinfo' : ''}" href="${esc(r.href)}"${r.sideInfo ? ` data-ft-item-id="${esc(r.id)}"` : ''}>${esc(r.label)}</a>`).join('');
		if (!rows) return '';
		const title = (block.rows || []).length === 1 ? block.singular : block.plural;
		return `<div class="list-group list-group-flush small mb-2 source-rail-credit-block rounded overflow-hidden border border-secondary border-opacity-50">
			<div class="list-group-item list-group-item-${esc(block.tone)} py-2 px-3 fw-semibold text-uppercase small d-flex align-items-center gap-2 source-rail-credit-block-header">${icon(block.icon, 18, 18)}<span>${esc(title)}</span></div>${rows}
		</div>`;
	}
	const text = block.itemId
		? `<button type="button" class="list-group-item py-2 px-3 text-start w-100 border-secondary border-opacity-25 source-rail-credit-block__text ft-source-rail-sideinfo" data-ft-item-id="${esc(block.itemId)}">${esc(block.label)}</button>`
		: `<div class="list-group-item py-2 px-3 border-secondary border-opacity-25 source-rail-credit-block__text">${esc(block.label)}</div>`;
	return `<div class="list-group list-group-flush small mb-2 source-rail-credit-block rounded overflow-hidden border border-secondary border-opacity-50">
		<div class="list-group-item list-group-item-${esc(block.tone)} py-2 px-3 fw-semibold text-uppercase small d-flex align-items-center gap-2 source-rail-credit-block-header">${icon(block.icon, 18, 18)}<span>${esc(block.title)}</span></div>${text}
	</div>`;
}

function renderToc(toc: TocEntry[]): string {
	if (!toc.length) return '';
	const chapters: Array<{ heading: TocEntry | null; children: TocEntry[] }> = [];
	let ci = -1;
	toc.forEach((e) => {
		const level = Number(e.level) || 2;
		if (level === 2) {
			chapters.push({ heading: e, children: [] });
			ci = chapters.length - 1;
		} else {
			if (ci < 0) { chapters.push({ heading: null, children: [] }); ci = 0; }
			chapters[ci]?.children.push(e);
		}
	});
	let n = 0;
	const body = chapters.map((ch, i) => {
		if (!ch.heading) {
			return `<ul class="list-unstyled mb-2 pb-2 border-bottom border-secondary border-opacity-25">${ch.children.map((e) => `<li class="mb-1" style="padding-left:${Math.max(0, Number(e.level) - 2)}rem"><a class="source-toc-link d-inline-block" href="#${esc(e.id)}" data-toc-target="${esc(e.id)}">${esc(e.text)}</a></li>`).join('')}</ul>`;
		}
		n += 1;
		const collapseId = `source-toc-c-${i}`;
		const kids = ch.children.map((e, k) => `<li class="mb-1" style="padding-left:${Math.max(0, Number(e.level) - 3)}rem"><a class="source-toc-link d-inline-block" href="#${esc(e.id)}" data-toc-target="${esc(e.id)}"><span class="source-toc-idx" aria-hidden="true">${n}.${k + 1}</span>${esc(e.text)}</a></li>`).join('');
		return `<div class="source-toc-chapter mb-1" data-toc-root-id="${esc(ch.heading.id)}"><div class="source-toc-chapter-head"><a class="source-toc-link source-toc-link--chapter flex-grow-1 min-w-0" href="#${esc(ch.heading.id)}" data-toc-target="${esc(ch.heading.id)}"><span class="source-toc-idx" aria-hidden="true">${n}.</span>${esc(ch.heading.text)}</a>${kids ? `<button type="button" class="source-toc-toggle ms-auto" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false" aria-controls="${collapseId}"><span class="source-toc-toggle-icon" aria-hidden="true">&#9660;</span></button>` : '<span class="source-toc-toggle-slot ms-auto" aria-hidden="true"></span>'}</div>${kids ? `<div id="${collapseId}" class="collapse source-toc-chapter-body" data-bs-parent="#sourceTocTree"><ul class="list-unstyled mb-0 pt-2">${kids}</ul></div>` : ''}</div>`;
	}).join('');
	return `<nav class="source-rail-panel source-rail-panel--toc" aria-labelledby="source-toc-heading-rail"><h2 id="source-toc-heading-rail" class="h6 text-uppercase text-muted mb-2">On this page</h2><div id="sourceTocTree" class="small">${body}</div></nav>`;
}

function renderAdventures(data: SourceDetailPayload): string {
	if (!data.adventures.length) return '';
	const cards = data.adventures.map((a) => {
		const card = renderAdventureCardElement(a, icon('level'));
		card.classList.remove('col-xl-2');
		card.classList.add('col-xl-3');
		return card.outerHTML;
	}).join('');
	return `<section id="sourceAdventures" class="source-adventures-section mt-4" aria-labelledby="source-adventures-heading">
		<div class="source-adventures-shell">
			<div class="ft-cat-toolbar source-adventures-heading d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3 mb-2">
				<div class="d-flex align-items-center gap-2 me-md-auto">
					${icon('adventure', 28, 28)}
					<div>
						<h2 id="source-adventures-heading" class="ft-cat-title h5 m-0">Source Adventures</h2>
						<p class="ft-cat-subtitle text-muted mb-0">${data.adventures.length} ${data.adventures.length === 1 ? 'adventure' : 'adventures'} from this source.</p>
					</div>
				</div>
				<span class="badge bg-dark border border-secondary align-self-start align-self-md-center">${data.adventures.length}</span>
			</div>
			<div class="row g-3 source-adventure-grid">${cards}</div>
		</div>
	</section>`;
}

function renderPage(data: SourceDetailPayload): string {
	const pubs = data.publisherLine[0]?.rows || [];
	const from = pubs.length ? `<div class="mb-2"><h6>From: ${pubs.map((p) => `<a class="link-primary" href="${esc(p.href)}">${esc(p.label)}</a>`).join(' ')}</h6></div>` : '';
	return `<div class="row g-3 g-lg-4 source-detail-layout pt-3">
		<div class="col-12 col-lg-3 text-left px-0 mb-3 mb-lg-0 source-detail-rail-col">
			<button type="button" class="source-mobile-rail-tab btn btn-primary text-dark d-lg-none" data-bs-toggle="offcanvas" data-bs-target="#sourceContextRail" aria-controls="sourceContextRail">Source</button>
			<div class="offcanvas-lg offcanvas-end text-bg-dark" tabindex="-1" id="sourceContextRail" aria-labelledby="sourceContextRailLabel">
				<div class="offcanvas-header border-bottom border-secondary d-lg-none"><h2 class="offcanvas-title h5 mb-0" id="sourceContextRailLabel">${esc(data.source.name)}</h2><button type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas" aria-label="Close"></button></div>
				<div class="offcanvas-body p-3 p-lg-0"><div class="source-rail-panel source-rail-panel--info">${renderMedia(data)}${renderPriceBlock(data)}${renderAssets(data)}${renderAdventureContents(data)}<div class="source-rail-credits-stack mt-2">${data.rail.credits.map(renderCreditBlock).join('')}</div></div>${renderToc(data.publication.toc || [])}</div>
			</div>
		</div>
		<div class="col-12 col-lg-9 text-left p-2 pe-4 pe-lg-2 source-detail-main">
			<div class="border-bottom"><div><h2 class="source-detail-title">${esc(data.source.name)}</h2></div>${from}${data.publication.html ? `<article class="source-publication-body mb-4" aria-label="Publication contents">${data.publication.html}</article>` : ''}</div>
			${renderAdventures(data)}
		</div>
	</div>`;
}

function hostFromEvent(e: Event): Element | null {
	const target = e.target;
	return target instanceof Element ? target : (target instanceof Node ? target.parentElement : null);
}

function initRailOffcanvas(root: HTMLElement): void {
	const rail = root.querySelector('#sourceContextRail') as HTMLElement | null;
	const Offcanvas = window.bootstrap?.Offcanvas;
	if (!rail || !Offcanvas) return;
	const hideRail = () => Offcanvas.getOrCreateInstance(rail).hide();
	rail.querySelectorAll('[data-bs-dismiss="offcanvas"]').forEach((btn) => btn.addEventListener('click', (e) => { e.preventDefault(); hideRail(); }));
	rail.querySelectorAll('a[href^="#"]').forEach((link) => link.addEventListener('click', () => {
		if (window.matchMedia('(max-width: 991.98px)').matches) hideRail();
	}));
}

function openItemDetail(itemId: string, rootUrl: string): void {
	if (!itemId) return;
	if (typeof window.openItemInfo === 'function' && typeof window.getSidelogContext === 'function' && window.getSidelogContext()) {
		window.openItemInfo(itemId);
		return;
	}
	const openUrl = `${rootUrl}item.php?id=${encodeURIComponent(itemId)}`;
	if (typeof window.openSideInfoPanel !== 'function' || typeof window.queryData !== 'function') {
		window.location.href = openUrl;
		return;
	}
	const base = typeof window.BASE_URL !== 'undefined' ? String(window.BASE_URL) : rootUrl;
	const payload: Record<string, unknown> = { data_in_1: itemId, campaign_id: '', location_id: '', encounter_id: '', caster_id: '', caster_main_id: '' };
	const sl = typeof window.getSidelogContext === 'function' ? window.getSidelogContext() : null;
	if (sl) Object.assign(payload, { campaign_id: sl.campaign_id || '', location_id: sl.location_id || '', encounter_id: sl.encounter_id || '', caster_id: sl.caster_id || '', caster_main_id: sl.caster_main_id || '' });
	if (typeof window.forceOpenSideInfoPanel === 'function') window.forceOpenSideInfoPanel(); else window.openSideInfoPanel();
	if (typeof window.addToSearchHistory === 'function') window.addToSearchHistory(itemId);
	window.queryData(`${base}options/tables/open_table_item_inc.php`, 'div_refresh_side_info', payload);
}

function initItemRefs(root: HTMLElement, data: SourceDetailPayload): void {
	const article = root.querySelector('article.source-publication-body') as HTMLElement | null;
	const ids = article ? Array.from(new Set(Array.from(article.querySelectorAll('.ft-item-ref[data-ft-item-id]')).map((el) => String(el.getAttribute('data-ft-item-id') || '').trim()).filter(Boolean))) : [];
	if (article && ids.length) {
		fetch(`${data.rootUrl}options/tools/items/item_names_by_ids_inc.php?ids=${encodeURIComponent(ids.join(','))}`, { credentials: 'same-origin' })
			.then((r) => r.json())
			.then((map) => {
				if (!map || typeof map !== 'object') return;
				article.querySelectorAll('.ft-item-ref[data-ft-item-id]').forEach((el) => {
					const id = String(el.getAttribute('data-ft-item-id') || '').trim();
					if (!id || !Object.prototype.hasOwnProperty.call(map, id) || !map[id]) return;
					const label = el.querySelector('.ft-item-ref-label');
					if (label) label.textContent = map[id]; else el.textContent = map[id];
				});
			}).catch(() => {});
	}
	const activate = (e: MouseEvent | KeyboardEvent) => {
		const host = hostFromEvent(e);
		const el = host?.closest('.ft-item-ref[data-ft-item-id], .ft-source-rail-sideinfo[data-ft-item-id]') as HTMLElement | null;
		if (!el || !root.contains(el)) return;
		if (e instanceof MouseEvent && (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)) return;
		if (e instanceof KeyboardEvent && e.key !== 'Enter' && e.key !== ' ') return;
		e.preventDefault();
		openItemDetail(String(el.getAttribute('data-ft-item-id') || '').trim(), data.rootUrl);
	};
	root.addEventListener('click', activate as EventListener);
	root.addEventListener('keydown', activate as EventListener);
}

function initToc(root: HTMLElement): void {
	const article = root.querySelector('article.source-publication-body');
	const tocPanel = root.querySelector('.source-rail-panel--toc') as HTMLElement | null;
	const tocTree = root.querySelector('#sourceTocTree') as HTMLElement | null;
	const Collapse = window.bootstrap?.Collapse;
	if (!article || !tocPanel || !tocTree || !Collapse) return;
	const headings = Array.from(article.querySelectorAll('h2[id], h3[id], h4[id]')) as HTMLElement[];
	const links = Array.from(tocTree.querySelectorAll('a.source-toc-link[data-toc-target]')) as HTMLAnchorElement[];
	if (!headings.length || !links.length) return;
	const offset = () => 98;
	const find = (id: string) => links.find((l) => l.getAttribute('data-toc-target') === id) || null;
	const apply = (id: string) => {
		links.forEach((l) => l.classList.remove('is-active', 'is-active-branch'));
		const match = find(id);
		match?.classList.add('is-active');
		if (match) {
			const col = match.closest('.source-toc-chapter-body.collapse') as HTMLElement | null;
			if (col && !col.classList.contains('show')) Collapse.getOrCreateInstance(col).show();
		}
	};
	let scheduled = false;
	let last = '';
	const tick = () => {
		scheduled = false;
		let active = headings[0]?.id || '';
		headings.forEach((h) => { if (h.getBoundingClientRect().top <= offset()) active = h.id; });
		if (active && active !== last) { last = active; apply(active); }
	};
	const queue = () => { if (!scheduled) { scheduled = true; requestAnimationFrame(tick); } };
	window.addEventListener('scroll', queue, { passive: true });
	window.addEventListener('resize', queue);
	tick();
}

function initCommerce(root: HTMLElement, data: SourceDetailPayload): void {
	const box = root.querySelector('#ftSourceCommerceActions') as HTMLElement | null;
	if (!box) return;
	const show = (selector: string, visible: boolean) => box.querySelectorAll(selector).forEach((el) => el.classList.toggle('d-none', !visible));
	const busy = (value: boolean) => box.querySelectorAll('button').forEach((btn) => { (btn as HTMLButtonElement).disabled = value; });
	const syncBadge = (hrefSuffix: string, count: number) => {
		document.querySelectorAll(`a.dropdown-item[href$="${hrefSuffix}"]`).forEach((link) => {
			let badge = link.querySelector('.badge');
			if (count > 0) {
				if (!badge) {
					badge = document.createElement('span');
					badge.className = 'badge bg-secondary rounded-pill';
					link.appendChild(document.createTextNode(' '));
					link.appendChild(badge);
				}
				badge.textContent = String(count);
			} else if (badge) {
				const prev = badge.previousSibling;
				if (prev && prev.nodeType === 3 && /^\s$/.test(String(prev.nodeValue || ''))) prev.remove();
				badge.remove();
			}
		});
	};
	const toggle = async (action: string) => {
		busy(true);
		try {
			const resp = await fetch(`${data.rootUrl}options/tools/sources/source_commerce_toggle_inc.php`, {
				method: 'POST',
				credentials: 'same-origin',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ source_id: data.source.id, action }),
			});
			const j = await resp.json().catch(() => null);
			if (!j || j.ok !== true) {
				console.warn('[source-detail] commerce update failed', j);
				return;
			}
			show('[data-ft-commerce-state="cart-out"]', !j.in_cart);
			show('[data-ft-commerce-state="cart-in"]', !!j.in_cart);
			show('[data-ft-commerce-state="wish-out"]', !j.in_wishlist);
			show('[data-ft-commerce-state="wish-in"]', !!j.in_wishlist);
			if (typeof j.cart_count === 'number') syncBadge('my_cart.php', j.cart_count);
			if (typeof j.wishlist_count === 'number') syncBadge('my_wishlist.php', j.wishlist_count);
		} finally {
			busy(false);
		}
	};
	box.addEventListener('click', (e) => {
		const ctl = hostFromEvent(e)?.closest('.ft-src-add-cart, .ft-src-remove-cart, .ft-src-add-wishlist, .ft-src-remove-wishlist') as HTMLElement | null;
		if (!ctl || ctl.tagName !== 'BUTTON') return;
		e.preventDefault();
		if (ctl.classList.contains('ft-src-add-cart')) void toggle('add_cart');
		else if (ctl.classList.contains('ft-src-remove-cart')) void toggle('remove_cart');
		else if (ctl.classList.contains('ft-src-add-wishlist')) void toggle('add_wishlist');
		else void toggle('remove_wishlist');
	});
}

function initFreeLibrary(root: HTMLElement, data: SourceDetailPayload): void {
	const box = root.querySelector('#ftSourceFreeLibraryActions') as HTMLElement | null;
	if (!box) return;
	const button = box.querySelector('.ft-src-add-free-library') as HTMLButtonElement | null;
	const link = box.querySelector('.ft-src-view-library') as HTMLElement | null;
	if (!button) return;
	const syncBadge = (hrefSuffix: string, count: number) => {
		document.querySelectorAll(`a.dropdown-item[href$="${hrefSuffix}"]`).forEach((navLink) => {
			let badge = navLink.querySelector('.badge');
			if (count > 0) {
				if (!badge) {
					badge = document.createElement('span');
					badge.className = 'badge bg-secondary rounded-pill';
					navLink.appendChild(document.createTextNode(' '));
					navLink.appendChild(badge);
				}
				badge.textContent = String(count);
			} else if (badge) {
				const prev = badge.previousSibling;
				if (prev && prev.nodeType === 3 && /^\s$/.test(String(prev.nodeValue || ''))) prev.remove();
				badge.remove();
			}
		});
	};
	button.addEventListener('click', async (e) => {
		e.preventDefault();
		if (button.disabled) return;
		button.disabled = true;
		try {
			const resp = await fetch(`${data.rootUrl}options/tools/sources/add_free_to_library_inc.php`, {
				method: 'POST',
				credentials: 'same-origin',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ source_id: data.source.id }),
			});
			const j = await resp.json().catch(() => null);
			if (!j || j.ok !== true) {
				button.disabled = false;
				return;
			}
			button.classList.add('d-none');
			link?.classList.remove('d-none');
			if (typeof j.cart_count === 'number') syncBadge('my_cart.php', j.cart_count);
			if (typeof j.wishlist_count === 'number') syncBadge('my_wishlist.php', j.wishlist_count);
		} catch {
			button.disabled = false;
		}
	});
}

export function mountSourceDetailPage(): void {
	const data = readPayload();
	const root = document.getElementById('ft-source-detail-root');
	if (!data || !root) return;
	const w = window as Window & { ftSetActiveEncounterId?: (id: string, source?: string) => string };
	if (typeof w.ftSetActiveEncounterId === 'function') {
		w.ftSetActiveEncounterId('', 'source-detail-page');
	}
	root.innerHTML = renderPage(data);
	if (typeof window.ftMountIcons === 'function') window.ftMountIcons(root);
	initRailOffcanvas(root);
	initItemRefs(root, data);
	initToc(root);
	initCommerce(root, data);
	initFreeLibrary(root, data);
	if (typeof window.initializeVideoCards === 'function') window.initializeVideoCards();
}

window.ftMountSourceDetailPage = mountSourceDetailPage;
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', mountSourceDetailPage, { once: true });
} else {
	mountSourceDetailPage();
}
